"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Ce que l'on peut mettre en ligne. Couleurs en toutes lettres pour Tailwind.
const DOMAINS = [
    {
        icon: "user-rounded-bold-duotone",
        titleKey: "GoLive_everywhere_c1_t",
        descKey: "GoLive_everywhere_c1_d",
        bg: "bg-sky-500/15",
        ring: "ring-sky-400/30",
        text: "text-sky-300",
    },
    {
        icon: "cart-large-bold-duotone",
        titleKey: "GoLive_everywhere_c2_t",
        descKey: "GoLive_everywhere_c2_d",
        bg: "bg-emerald-500/15",
        ring: "ring-emerald-400/30",
        text: "text-emerald-300",
    },
    {
        icon: "notebook-bold-duotone",
        titleKey: "GoLive_everywhere_c3_t",
        descKey: "GoLive_everywhere_c3_d",
        bg: "bg-violet-500/15",
        ring: "ring-violet-400/30",
        text: "text-violet-300",
    },
    {
        icon: "chef-hat-bold-duotone",
        titleKey: "GoLive_everywhere_c4_t",
        descKey: "GoLive_everywhere_c4_d",
        bg: "bg-amber-500/15",
        ring: "ring-amber-400/30",
        text: "text-amber-300",
    },
    {
        icon: "users-group-rounded-bold-duotone",
        titleKey: "GoLive_everywhere_c5_t",
        descKey: "GoLive_everywhere_c5_d",
        bg: "bg-rose-500/15",
        ring: "ring-rose-400/30",
        text: "text-rose-300",
    },
    {
        icon: "document-text-bold-duotone",
        titleKey: "GoLive_everywhere_c6_t",
        descKey: "GoLive_everywhere_c6_d",
        bg: "bg-cyan-500/15",
        ring: "ring-cyan-400/30",
        text: "text-cyan-300",
    },
    {
        icon: "window-frame-bold-duotone",
        titleKey: "GoLive_everywhere_c7_t",
        descKey: "GoLive_everywhere_c7_d",
        bg: "bg-indigo-500/15",
        ring: "ring-indigo-400/30",
        text: "text-indigo-300",
    },
    {
        icon: "calendar-bold-duotone",
        titleKey: "GoLive_everywhere_c8_t",
        descKey: "GoLive_everywhere_c8_d",
        bg: "bg-orange-500/15",
        ring: "ring-orange-400/30",
        text: "text-orange-300",
    },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function GoLiveEverywhere() {
    const t = useTranslations();

    return (
        <section id="golive-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("GoLive_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("GoLive_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows (teinte mise en ligne) */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-cyan-500/15 ring-1 ring-cyan-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="global-bold-duotone"
                            className="w-3.5 h-3.5 text-cyan-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300">
                            {t("GoLive_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("GoLive_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-cyan-300 to-sky-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des usages */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5"
                    >
                        {DOMAINS.map((domain) => (
                            <motion.div
                                key={domain.titleKey}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div
                                    className={`w-11 h-11 rounded-xl ${domain.bg} ring-1 ${domain.ring} flex items-center justify-center mb-4`}
                                >
                                    <SolarIcon
                                        name={domain.icon}
                                        className={`w-5 h-5 ${domain.text}`}
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(domain.titleKey)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(domain.descKey)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("GoLive_everywhere_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
